import type { OtpRequestResult } from '@erp/shared';
import { ArrowLeft, KeyRound, Loader2, Smartphone } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import { useStorefront } from '../../app/StorefrontLayout.js';
import { Button, FormField, Input } from '../../components/ui/index.js';
import { useCustomerAuthStore, type PortalCustomer } from '../../lib/customerAuthStore.js';
import { publicApi } from '../../lib/publicApi.js';

import { PhoneInput } from './PhoneInput.js';

interface AuthResponse {
  tokens: { accessToken: string; expiresIn: number };
  customer: PortalCustomer;
}

/**
 * Storefront sign-in. PRIMARY method is phone + password
 * (POST /:slug/auth/login); WhatsApp OTP is offered as a secondary option when
 * the store has it enabled. Both mint the same portal session.
 */
export function StorefrontLoginPage(): JSX.Element {
  const { t } = useTranslation('cms');
  const { slug, site } = useStorefront();
  const navigate = useNavigate();
  const signedIn = useCustomerAuthStore((s) => !!s.accessToken);

  const [method, setMethod] = useState<'password' | 'otp' | 'reset' | 'register'>('password');
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  // '' = normal user (no price-list request); an id = a customer type awaiting staff approval.
  const [customerType, setCustomerType] = useState('');
  const [pendingNotice, setPendingNotice] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  useEffect(() => { if (method !== 'password' && step === 'code') codeRef.current?.focus(); }, [method, step]);

  if (signedIn) return <Navigate to="/portal/dashboard" replace />;

  const landSession = (data: AuthResponse): void => {
    useCustomerAuthStore.getState().setSession({
      accessToken: data.tokens.accessToken,
      expiresIn: data.tokens.expiresIn,
      customer: data.customer,
    });
    navigate('/portal/dashboard');
  };

  const loginPassword = async (): Promise<void> => {
    if (!phone.trim() || !password.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await publicApi().post<AuthResponse>(
        `/${slug}/auth/login`,
        { phone: phone.trim(), password },
        { withCredentials: true },
      );
      landSession(r.data);
    } catch { setError(t('login.invalid', { defaultValue: 'Wrong phone or password.' })); }
    finally { setBusy(false); }
  };

  const sendCode = async (): Promise<void> => {
    if (!phone.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await publicApi().post<OtpRequestResult>(`/${slug}/auth/otp/request`, { phone: phone.trim() });
      // No account for this number → steer to registration instead of a code screen
      // that would wait for a code that never comes (phone carries over).
      if (r.data.registered === false) {
        setMethod('register');
        setError(t('login.noAccount', { defaultValue: 'No account found for this number. Create one to continue.' }));
        return;
      }
      setStep('code');
      setCooldown(Math.min(60, r.data.ttlSeconds || 60));
    } catch { setError(t('login.sendFailed', { defaultValue: 'Could not send a code. Try again shortly.' })); }
    finally { setBusy(false); }
  };

  const verify = async (): Promise<void> => {
    if (code.trim().length < 4 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await publicApi().post<AuthResponse>(
        `/${slug}/auth/otp/verify`,
        { phone: phone.trim(), code: code.trim() },
        { withCredentials: true },
      );
      landSession(r.data);
    } catch { setError(t('login.codeFailed', { defaultValue: 'That code is invalid or expired.' })); }
    finally { setBusy(false); }
  };

  const STRONG = /^.{6,}$/;

  /** Send (or resend) the WhatsApp verification code for registration. */
  const sendRegisterCode = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const r = await publicApi().post<OtpRequestResult>(`/${slug}/auth/register/otp/request`, { phone: phone.trim() });
      // Already has an account: no code was sent, so stay on the phone step and say
      // so here rather than after they have waited for and typed out a code that
      // was only ever going to be refused.
      if (r.data.registered) {
        setError(t('login.phoneTaken', { defaultValue: 'An account with this phone already exists — sign in instead.' }));
        return;
      }
      setStep('code');
      setCode('');
      setCooldown(Math.min(60, r.data.ttlSeconds || 60));
    } catch { setError(t('login.sendFailed', { defaultValue: 'Could not send a code. Try again shortly.' })); }
    finally { setBusy(false); }
  };

  const register = async (): Promise<void> => {
    if (!name.trim() || !phone.trim() || busy) return;
    if (!STRONG.test(password)) { setError(t('login.weakPassword', { defaultValue: 'Password must be at least 6 characters.' })); return; }
    // WhatsApp phone verification first (when the store has the otp module):
    // the account is only created with a valid code.
    if (site.otpEnabled && step === 'phone') { await sendRegisterCode(); return; }
    setBusy(true);
    setError(null);
    try {
      const r = await publicApi().post<AuthResponse | { pending: true }>(
        `/${slug}/auth/register`,
        {
          name: name.trim(),
          phone: phone.trim(),
          password,
          ...(customerType ? { priceListId: customerType } : {}),
          ...(site.otpEnabled && code.trim() ? { otpCode: code.trim() } : {}),
        },
        { withCredentials: true },
      );
      if ('pending' in r.data && r.data.pending) {
        // Special customer types need staff approval before the account can sign in.
        setPendingNotice(true);
        setMethod('password');
        setStep('phone');
        setPassword('');
        return;
      }
      landSession(r.data as AuthResponse);
    } catch (e) {
      const status = (e as { response?: { status?: number } }).response?.status;
      const codeErr = (e as { response?: { data?: { cause?: { code?: string } } } }).response?.data?.cause?.code;
      setError(status === 409
        ? t('login.phoneTaken', { defaultValue: 'An account with this phone already exists — sign in instead.' })
        : codeErr?.startsWith('otp.')
          ? t('login.codeFailed', { defaultValue: 'That code is invalid or expired.' })
          : t('login.registerFailed', { defaultValue: 'Could not create the account. Check the details and try again.' }));
    } finally { setBusy(false); }
  };

  const resetConfirm = async (): Promise<void> => {
    if (code.trim().length < 4 || busy) return;
    if (!STRONG.test(newPassword)) { setError(t('login.weakPassword', { defaultValue: 'Password must be at least 6 characters.' })); return; }
    setBusy(true);
    setError(null);
    try {
      const r = await publicApi().post<AuthResponse>(
        `/${slug}/auth/reset/confirm`,
        { phone: phone.trim(), code: code.trim(), password: newPassword },
        { withCredentials: true },
      );
      landSession(r.data);
    } catch { setError(t('login.codeFailed', { defaultValue: 'That code is invalid or expired.' })); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-6">
      <Helmet><title>{`${t('login.signIn', { defaultValue: 'Sign in' })} · ${site.storeName}`}</title></Helmet>

      <div className="rounded-2xl border border-border bg-bg-subtle p-6 sm:p-8">
        <div className="mb-5 flex flex-col items-center gap-2 text-center">
          <span className="grid h-12 w-12 place-items-center rounded-full bg-brand/10 text-brand">
            {method === 'otp' ? <Smartphone className="h-6 w-6" aria-hidden /> : <KeyRound className="h-6 w-6" aria-hidden />}
          </span>
          <h1 className="text-xl font-semibold">
            {method === 'register'
              ? t('login.createTitle', { defaultValue: 'Create an account' })
              : t('login.titlePhone', { defaultValue: 'Sign in' })}
          </h1>
          <p className="text-sm text-fg-muted">
            {method === 'register'
              ? t('login.registerHint', { defaultValue: 'Shop faster and track your orders and balance.' })
              : method === 'password'
                ? t('login.passwordHint', { defaultValue: 'Enter your phone number and password.' })
                : step === 'phone'
                  ? t('login.phoneHint', { defaultValue: 'We will send a one-time code to your WhatsApp.' })
                  : t('login.codeHint', { defaultValue: 'Enter the code we sent to {phone}.', phone })}
          </p>
        </div>

        {pendingNotice && (
          <p className="mb-4 rounded-lg bg-warning/10 px-3 py-2 text-sm text-warning" role="status">
            {t('login.pendingApproval', { defaultValue: 'Your account was created and is awaiting approval. You will be able to sign in once the store confirms your customer type.' })}
          </p>
        )}

        {method === 'register' && step === 'code' ? (
          <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); void register(); }}>
            <p className="text-sm text-fg-muted">{t('login.registerCodeHint', { defaultValue: 'We sent a WhatsApp code to {{phone}} — enter it to confirm your number.', phone })}</p>
            <FormField id="reg-code" label={t('login.code', { defaultValue: 'Verification code' })} required>
              <Input
                id="reg-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="text-center text-lg font-mono tracking-[0.4em]"
                maxLength={8}
                autoFocus
              />
            </FormField>
            {error && <p className="text-sm text-danger" role="alert">{error}</p>}
            <Button type="submit" size="lg" loading={busy} disabled={code.trim().length < 4}>
              {t('login.registerVerify', { defaultValue: 'Verify & create account' })}
            </Button>
            <div className="flex items-center justify-between text-sm">
              <button type="button" onClick={() => { setStep('phone'); setCode(''); setError(null); }} className="inline-flex items-center gap-1 text-fg-muted transition hover:text-fg">
                <ArrowLeft className="h-3.5 w-3.5 rtl:rotate-180" aria-hidden />{t('login.changeNumber', { defaultValue: 'Change number' })}
              </button>
              {cooldown > 0 ? (
                <span className="inline-flex items-center gap-1 text-fg-muted"><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />{t('login.resendIn', { defaultValue: 'Resend in {s}s', s: cooldown })}</span>
              ) : (
                <button type="button" onClick={() => void sendRegisterCode()} className="font-medium text-brand hover:underline">{t('login.resend', { defaultValue: 'Resend code' })}</button>
              )}
            </div>
          </form>
        ) : method === 'register' ? (
          <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); void register(); }}>
            <FormField id="reg-name" label={t('login.name', { defaultValue: 'Full name' })} required>
              <Input id="reg-name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            </FormField>
            <FormField id="reg-phone" label={t('login.phone', { defaultValue: 'Phone number' })} required>
              <PhoneInput id="reg-phone" value={phone} onChange={setPhone} />
            </FormField>
            <FormField id="reg-password" label={t('login.password', { defaultValue: 'Password' })} helperText={t('login.passwordRules', { defaultValue: 'At least 6 characters.' })} required>
              <Input id="reg-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </FormField>
            {site.customerTypes.length > 0 && (
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium">{t('login.customerType', { defaultValue: 'Account type' })}</span>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setCustomerType('')}
                    className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition ${customerType === '' ? 'border-brand bg-brand text-brand-contrast' : 'border-border text-fg-muted hover:border-brand/40'}`}
                  >
                    {t('login.typeNormal', { defaultValue: 'Normal user' })}
                  </button>
                  {site.customerTypes.map((ct) => (
                    <button
                      key={ct.id}
                      type="button"
                      onClick={() => setCustomerType(ct.id)}
                      className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition ${customerType === ct.id ? 'border-brand bg-brand text-brand-contrast' : 'border-border text-fg-muted hover:border-brand/40'}`}
                    >
                      {ct.name}
                    </button>
                  ))}
                </div>
                {customerType !== '' && (
                  <p className="text-xs text-fg-muted">{t('login.typePendingNote', { defaultValue: 'This account type needs the store’s approval before you can sign in.' })}</p>
                )}
              </div>
            )}
            {error && <p className="text-sm text-danger" role="alert">{error}</p>}
            <Button type="submit" size="lg" loading={busy} disabled={!name.trim() || !phone.trim() || password.length < 6}>
              {t('login.registerSubmit', { defaultValue: 'Create account' })}
            </Button>
            <button type="button" onClick={() => { setMethod('password'); setError(null); }} className="text-sm font-medium text-brand hover:underline">
              {t('login.haveAccount', { defaultValue: 'Already have an account? Sign in' })}
            </button>
          </form>
        ) : method === 'password' ? (
          <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); void loginPassword(); }}>
            <FormField id="login-phone" label={t('login.phone', { defaultValue: 'Phone number' })} required>
              <PhoneInput id="login-phone" value={phone} onChange={setPhone} autoFocus />
            </FormField>
            <FormField id="login-password" label={t('login.password', { defaultValue: 'Password' })} required>
              <Input id="login-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </FormField>
            {error && <p className="text-sm text-danger" role="alert">{error}</p>}
            <Button type="submit" size="lg" loading={busy} disabled={!phone.trim() || !password.trim()}>{t('login.submit', { defaultValue: 'Sign in' })}</Button>
            <div className="flex flex-col gap-2">
              <button type="button" onClick={() => { setMethod('register'); setPendingNotice(false); setError(null); }} className="text-sm font-semibold text-brand hover:underline">
                {t('login.createAccount', { defaultValue: 'New here? Create an account' })}
              </button>
              {site.otpEnabled && (
                <>
                  <button type="button" onClick={() => { setMethod('otp'); setStep('phone'); setError(null); }} className="text-sm font-medium text-brand hover:underline">
                    {t('login.useCode', { defaultValue: 'Sign in with a WhatsApp code instead' })}
                  </button>
                  <button type="button" onClick={() => { setMethod('reset'); setStep('phone'); setCode(''); setNewPassword(''); setError(null); }} className="text-sm font-medium text-fg-muted hover:text-fg hover:underline">
                    {t('login.forgot', { defaultValue: 'Forgot your password?' })}
                  </button>
                </>
              )}
            </div>
          </form>
        ) : step === 'phone' ? (
          <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); void sendCode(); }}>
            <FormField id="otp-phone" label={t('login.phone', { defaultValue: 'Phone number' })} required>
              <PhoneInput id="otp-phone" value={phone} onChange={setPhone} autoFocus />
            </FormField>
            {error && <p className="text-sm text-danger" role="alert">{error}</p>}
            <Button type="submit" size="lg" loading={busy} disabled={!phone.trim()}>{t('login.sendCode', { defaultValue: 'Send code' })}</Button>
            <button type="button" onClick={() => { setMethod('password'); setError(null); }} className="text-sm font-medium text-brand hover:underline">
              {t('login.usePassword', { defaultValue: 'Sign in with a password instead' })}
            </button>
          </form>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); void (method === 'reset' ? resetConfirm() : verify()); }}>
            <FormField id="otp-code" label={t('login.code', { defaultValue: 'Verification code' })} required>
              <Input
                ref={codeRef}
                id="otp-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="text-center text-lg font-mono tracking-[0.4em]"
                maxLength={8}
              />
            </FormField>
            {method === 'reset' && (
              <FormField id="reset-newpw" label={t('login.newPassword', { defaultValue: 'New password' })} helperText={t('login.passwordRules', { defaultValue: 'At least 6 characters.' })} required>
                <Input id="reset-newpw" type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
              </FormField>
            )}
            {error && <p className="text-sm text-danger" role="alert">{error}</p>}
            <Button type="submit" size="lg" loading={busy} disabled={code.trim().length < 4 || (method === 'reset' && newPassword.length < 6)}>
              {method === 'reset' ? t('login.resetSubmit', { defaultValue: 'Reset & sign in' }) : t('login.verify', { defaultValue: 'Verify & sign in' })}
            </Button>
            <div className="flex items-center justify-between text-sm">
              <button type="button" onClick={() => { setStep('phone'); setCode(''); setError(null); }} className="inline-flex items-center gap-1 text-fg-muted transition hover:text-fg">
                <ArrowLeft className="h-3.5 w-3.5" aria-hidden />{t('login.changeNumber', { defaultValue: 'Change number' })}
              </button>
              {cooldown > 0 ? (
                <span className="inline-flex items-center gap-1 text-fg-muted"><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />{t('login.resendIn', { defaultValue: 'Resend in {s}s', s: cooldown })}</span>
              ) : (
                <button type="button" onClick={() => void sendCode()} className="font-medium text-brand hover:underline">{t('login.resend', { defaultValue: 'Resend code' })}</button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
